// Quiz Deck: flip cards fed by every science module. Reading level follows her rung.

import { h, clear } from '@kit/dom';
import { shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { QUIZ, MODULE_SKILL, tierFor, type QuizItem } from '@content/science/quiz';

const CARDS_PER_ROUND = 8;

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  // the deck prefers this tier's reading level, then pads from the neighbors
  const same = shuffle(ctx.rng, QUIZ.filter(q => q.level === tier));
  const near = shuffle(ctx.rng, QUIZ.filter(q => q.level !== tier && Math.abs(q.level - tier) === 1));
  const picked: QuizItem[] = [];
  const modulesSeen = new Map<string, number>();
  for (const q of [...same, ...near]) {
    if (picked.length >= CARDS_PER_ROUND) break;
    if ((modulesSeen.get(q.module) || 0) >= 2) continue;   // spread across modules
    picked.push(q); modulesSeen.set(q.module, (modulesSeen.get(q.module) || 0) + 1);
  }
  let i = 0;
  let attempts = 0, correct = 0;
  let flipped = false, answered = false, wrongOnce = false;
  const t0 = performance.now();
  let tCard = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Card ', h('strong', null, '1'), ` of ${picked.length}`), h('button.btn.small.ghost', { onclick: () => ctx.speak(cur().q) }, 'Read it'));
  const card = h('div.card', { style: { minHeight: '150px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', borderTop: '8px solid var(--science)', cursor: 'pointer' }, onclick: () => flip() });
  const choices = h('div.stack');
  const why = h('p.center.muted.small', { style: { minHeight: '40px' } });
  const nextBtn = h('button.btn.primary.block', { onclick: next, style: { display: 'none' } }, 'Next card');
  host.append(head, card, choices, why, nextBtn);

  function cur(): QuizItem { return picked[i]; }

  function render() {
    const q = cur();
    (head.querySelector('strong') as HTMLElement).textContent = String(i + 1);
    clear(card);
    card.append(h('p.small.muted', null, q.module.replace('_', ' ')), h('p', { style: { fontSize: '20px', fontWeight: '800', marginTop: '6px' } }, q.q));
    if (!flipped) card.append(h('p.small', { style: { marginTop: '10px', color: 'var(--accent)', fontWeight: '800' } }, 'Tap to see the choices'));
    clear(choices);
    if (flipped) {
      const order = shuffle(ctx.rng, [0, 1, 2, 3]);
      for (const k of order) choices.append(h('button.choice', { style: { fontSize: '17px', minHeight: '52px', padding: '8px 12px' }, onclick: (e: Event) => answer(k, e.currentTarget as HTMLElement) }, q.choices[k]));
    }
    why.textContent = '';
    nextBtn.style.display = 'none';
  }

  function flip() { if (flipped) return; flipped = true; ctx.sound('pop'); render(); }

  function answer(k: number, el: HTMLElement) {
    if (answered) return;
    const q = cur();
    if (k === q.answer) {
      answered = true;
      attempts++; if (!wrongOnce) correct++;
      ctx.report({ skill: MODULE_SKILL[q.module], level, correct: !wrongOnce, hintUsed: wrongOnce, ms: performance.now() - tCard, problemId: `quiz:${q.id}` });
      ctx.sound('ding');
      el.classList.add('good');
      why.textContent = q.why;
      ctx.say(wrongOnce ? 'That is it.' : 'Right!');
      for (const b of Array.from(choices.children)) (b as HTMLButtonElement).disabled = true;
      nextBtn.style.display = '';
      nextBtn.textContent = i + 1 >= picked.length ? 'Finish' : 'Next card';
    } else {
      ctx.sound('buzz');
      el.classList.add('bad');
      (el as HTMLButtonElement).disabled = true;
      if (!wrongOnce) { wrongOnce = true; ctx.say('Not that one. One more try.'); }
      else {
        // second miss: reveal and move on, counted wrong
        answered = true; attempts++;
        ctx.report({ skill: MODULE_SKILL[q.module], level, correct: false, hintUsed: true, ms: performance.now() - tCard, problemId: `quiz:${q.id}` });
        for (const b of Array.from(choices.children)) { const bb = b as HTMLButtonElement; bb.disabled = true; if (bb.textContent === q.choices[q.answer]) bb.classList.add('good'); }
        why.textContent = q.why;
        nextBtn.style.display = '';
        nextBtn.textContent = i + 1 >= picked.length ? 'Finish' : 'Next card';
      }
    }
  }

  function next() {
    i++;
    if (i >= picked.length) {
      if (attempts === correct && attempts >= 3) ctx.charm('charm.quiz');
      ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 });
      return;
    }
    flipped = false; answered = false; wrongOnce = false; tCard = performance.now();
    ctx.say('');
    render();
  }

  render();
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'science.quiz-deck',
  title: 'Quiz Deck',
  blurb: 'Flip a card, pick an answer. Rocks, fossils, atoms, animals, forces, space and weather.',
  category: 'science',
  skills: ['geology', 'paleontology', 'chemistry', 'biology', 'physics', 'space', 'earth_science'],
  levelRange: [1, 10],
  unlock: { stars: 6 },
  mount,
};
export default mod;
